import React, { useEffect, useState } from "react";
import Form from "antd/lib/form";
import { Col, Row } from "antd";
import {
	EWidgets,
	IFormProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import { ZForm } from "@organisms";
import { ZButton, ZTypography } from "@atoms";
import { EFontWeights } from "@atoms/ZTypography/interface";
import UsersActionCreators from "@/Actions/User";
import useDispatch from "@/Components/Utils/useDispatch";
import {
	AmazonMetadataResponse,
	AmazonLocaleValues
} from "@/Contracts/Settings/index";
import "./index.less";
import { AmazonDrawerFormValues } from "@/Components/SettingsPage/Drawers/AmazonDrawer/interface";
import { AmazonMarketPlaceURL } from "@/Contracts/Settings";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";

export const IntegrateAmazonForm: React.FC = () => {
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const [amazonMetaData, setAmazonMetaData] =
		useState<AmazonMetadataResponse | null>(null);
	const [isFieldTouched, setIsFieldTouched] = useState<boolean>(false);
	const [selectedLocaleList, setSelectedLocaleList] = useState<
		AmazonLocaleValues[]
	>([]);

	const { loggedInUserId, currentActiveDocument } = useSelector(
		(state: GlobalState) => {
			return {
				loggedInUserId: state.user.userData.id,
				currentActiveDocument: state.documents.currentActiveDocument
			};
		}
	);

	const fetchMetadata = async () => {
		try {
			const response = await dispatch(
				UsersActionCreators.getAmazonMetadata()
			);
			setAmazonMetaData(response.amazonMetaData);
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		fetchMetadata();
	}, []);

	const regionOptions = amazonMetaData?.regions.map(region => {
		return {
			label: region.display_name,
			value: region.region
		};
	});

	const localeOptions = selectedLocaleList.map(locale => {
		return {
			label: locale.display_name,
			value: locale.locale
		};
	});

	const isMoreThanOneLocale = localeOptions && localeOptions?.length > 1;

	const onFinish = (amazonDrawerFormValues: AmazonDrawerFormValues) => {
		try {
			const redirectToAmazonURLDomain =
				AmazonMarketPlaceURL[amazonDrawerFormValues.region];
			const redirectToAmazonURL = `${redirectToAmazonURLDomain}/b2b/abws/oauth?state=${loggedInUserId}:${
				amazonDrawerFormValues.region
			}:${
				isMoreThanOneLocale
					? amazonDrawerFormValues.locale
					: selectedLocaleList[0].locale
			}:${currentActiveDocument?.id}&&redirect_uri=${
				process.env.REACT_APP_API_HOST
			}/v2/callback/amazon/auth/&applicationId=amzn1.sp.solution.db0cd1c6-2797-4e9f-99fb-31b6ab7583d1`;
			window.location.assign(redirectToAmazonURL);
		} catch (error) {
			console.log(error);
		}
	};

	const handleChange = () => {
		setIsFieldTouched(true);
	};

	const handleRegionChange = (selectedValue: string) => {
		const selectedRegion = amazonMetaData?.regions?.find(
			region => region.region === selectedValue
		);
		setSelectedLocaleList(selectedRegion?.locales ?? []);
	};

	const sections: IFormSectionProps[] = [
		{
			isIndentFormItems: false,
			meta: {
				formItemLayout: [5, 19],
				fields: [
					{
						key: "region_description",
						render: () => (
							<Row>
								<Col span={24}>
									<ZTypography.ZText
										level={1}
										weight={EFontWeights.MEDIUM}
										className="mb-4 block"
									>
										{`Select ${
											isMoreThanOneLocale
												? "Region/Locale"
												: "Region"
										}`}
									</ZTypography.ZText>
								</Col>
								<Col span={24}>
									<ZTypography.ZText
										level={1}
										className="mb-4 block"
									>
										{`Please select the region ${
											isMoreThanOneLocale
												? "and locale "
												: ""
										}associated with your Amazon Business account.`}
									</ZTypography.ZText>
								</Col>
							</Row>
						)
					},
					{
						key: "region",
						label: "Region",
						widget: EWidgets.SELECT,
						required: true,
						message: "Please select a region",
						className: `${isMoreThanOneLocale ? "" : "mb-2"}`,
						widgetProps: {
							placeholder: "Select a Region",
							options: regionOptions,
							onChange: handleRegionChange
						}
					},
					...(isMoreThanOneLocale
						? [
								{
									key: "locale",
									label: "Locale",
									widget: EWidgets.SELECT,
									required: true,
									message: "Please select a locale",
									className: "mb-2",
									widgetProps: {
										placeholder: "Select a Locale",
										options: localeOptions
									}
								}
						  ]
						: [])
				]
			}
		}
	];

	const formProps: IFormProps = {
		onFinish: onFinish,
		key: "ConnectAmazonForm",
		sections,
		form,
		isCancelAllowed: false,
		isSubmitAllowed: false,
		onValuesChange: handleChange,
		formFooter: (
			<Row>
				<Col span={19} offset={5}>
					<ZButton
						type="primary"
						htmlType="submit"
						disabled={!isFieldTouched}
					>
						Connect
					</ZButton>
				</Col>
			</Row>
		)
	};

	return (
		<ZForm
			{...formProps}
			className="pt-6 pb-4 px-5 integrate-amazon-form"
		/>
	);
};
